import { DateTime } from 'luxon';
import DateRangeFilterComponent from 'pages/platform/dashboard/merchant/listing/partials/DateRangeFilterComponent';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/platform/hooks';
import { setListingPagePropsObj, setPaymentFilterObj } from 'redux/platform/slice/paymentSlice';
import { isValidObjectId } from 'utils/stringUtils';

import { Button, Checkbox, CheckboxGroup, Heading, Icon, Text } from '@limepayments/cosmic';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { PaymentFilterStatusObj } from '../../constants';
import { getInitialFilterState } from '../initialStates';
import { IPaymentFilterObj, IPaymentFilterObjError } from '../types';

const getInitialErrorState = () => {
  return {
    dateValueError: '',
    dateTypeCompareError: '',
    dateTypeFilterError: '',
    statusError: '',
    dateRangeError: '',
    dateSingleError: '',
    merchantIdError: '',
    orderIdError: '',
    txnIdError: '',
    amountError: '',
    internalOrderIdError: '',
  };
};

interface Props {
  modalToggler: () => void;
}

function FilterComponent({ modalToggler }: Props) {
  const dispatch = useAppDispatch();
  const { paymentFilterObj, listingPageProps, apiBaseUri } = useAppSelector((state) => ({
    paymentFilterObj: state.payment.paymentFilterObj,
    listingPageProps: state.payment.listingPageProps,
    apiBaseUri: state.config.apiBaseUri,
  }));

  const [filterObj, setFilterObj] = useState<IPaymentFilterObj>(paymentFilterObj);
  const [filterObjError, setFilterObjError] = useState<IPaymentFilterObjError>(getInitialErrorState());

  const filterChangeHandler = useCallback(
    (key: string, value: boolean | string | Date | Array<Date> | null | undefined | [Date | null, Date | null]) => {
      setFilterObj((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const filterSaveHandler = async () => {
    const filterObjErrorRef = { ...getInitialErrorState() };
    let hasError = false;
    let totalFiltersApplied = 0;

    if (filterObj.dateActiveChecked) {
      if (!filterObj.dateTypeCompareValue) {
        filterObjErrorRef.dateTypeCompareError = 'This field is required';
        hasError = true;
      }

      if (filterObj.dateTypeCompareValue === 'is_in_the_last') {
        if (!filterObj.dateTypeFilterValue) {
          filterObjErrorRef.dateTypeFilterError = 'This field is required';
          hasError = true;
        }

        if (!filterObj.dateValue) {
          filterObjErrorRef.dateValueError = 'This field is required';
          hasError = true;
        }
      }

      if (filterObj.dateTypeCompareValue === 'is_between') {
        if (!filterObj.dateRangeValue) {
          filterObjErrorRef.dateRangeError = 'This field is required';
          hasError = true;
        }
      }

      if (
        filterObj.dateTypeCompareValue === 'is_equal_to' ||
        filterObj.dateTypeCompareValue === 'is_after' ||
        filterObj.dateTypeCompareValue === 'is_before'
      ) {
        if (!filterObj.dateSingleValue) {
          filterObjErrorRef.dateSingleError = 'This field is required';
          hasError = true;
        }
      }

      if (filterObj.dateTypeCompareValue === 'is_in_the_last' && !filterObjErrorRef.dateValueError) {
        if (filterObj.dateTypeFilterValue === 'days' && parseInt(filterObj.dateValue) > 90) {
          filterObjErrorRef.dateValueError = 'Maximum 90 days allowed';
          hasError = true;
        }

        if (filterObj.dateTypeFilterValue === 'month' && parseInt(filterObj.dateValue) > 3) {
          filterObjErrorRef.dateValueError = 'Maximum 3 months allowed';
          hasError = true;
        }
      }

      if (
        filterObj.dateTypeCompareValue === 'is_between' &&
        Array.isArray(filterObj.dateRangeValue) &&
        !filterObjErrorRef.dateValueError
      ) {
        const fromDateObj = new Date(filterObj.dateRangeValue[0]);
        const toDateObj = new Date(filterObj.dateRangeValue[1]);

        const fromDateLuxonObj = DateTime.fromISO(fromDateObj.toISOString()).toUTC();
        const toDateLuxonObj = DateTime.fromISO(toDateObj.toISOString()).toUTC();

        const diff = toDateLuxonObj.diff(fromDateLuxonObj, ['years', 'months', 'days', 'hours']);

        if (diff.months > 3 && diff.days > 0) {
          filterObjErrorRef.dateValueError = 'Maximum 90 days allowed';
          hasError = true;
        }
      }

      totalFiltersApplied++;
    }

    if (filterObj.amountIdChecked) {
      if (!filterObj.amount) {
        filterObjErrorRef.amountError = 'This field is required';
        hasError = true;
      }

      totalFiltersApplied++;
    }

    if (filterObj.internalOrderIdChecked) {
      if (!filterObj.internalOrderId) {
        filterObjErrorRef.internalOrderIdError = 'This field is required';
        hasError = true;
      }

      totalFiltersApplied++;
    }

    if (filterObj.txnIdChecked) {
      if (!filterObj.txnId) {
        filterObjErrorRef.txnIdError = 'This field is required';
        hasError = true;
      } else {
        if (!(await isValidObjectId(filterObj.txnId, 'transaction', apiBaseUri))) {
          filterObjErrorRef.txnIdError = 'Invalid Transaction ID';
          hasError = true;
        }
      }

      totalFiltersApplied++;
    }

    if (filterObj.statusFilterChecked) {
      if (!filterObj.statusValue) {
        filterObjErrorRef.statusError = 'This field is required';
        hasError = true;
      }

      totalFiltersApplied++;
    }

    if (filterObj.merchantIdChecked) {
      if (!filterObj.merchantId) {
        filterObjErrorRef.merchantIdError = 'This field is required';
        hasError = true;
      } else {
        if (!(await isValidObjectId(filterObj.merchantId, 'merchant', apiBaseUri))) {
          filterObjErrorRef.merchantIdError = 'Invalid Merchant ID';
          hasError = true;
        }
      }

      totalFiltersApplied++;
    }

    if (filterObj.orderIdChecked) {
      if (!filterObj.orderId) {
        filterObjErrorRef.orderIdError = 'This field is required';
        hasError = true;
      } else {
        if (!(await isValidObjectId(filterObj.orderId, 'order', apiBaseUri))) {
          filterObjErrorRef.orderIdError = 'Invalid Order ID';
          hasError = true;
        }
      }

      totalFiltersApplied++;
    }

    if (hasError) {
      setFilterObjError(filterObjErrorRef);
      return;
    }

    filterObj.totalFiltersApplied = totalFiltersApplied;
    filterObj.saveTiggerAt = DateTime.now().toISO();

    if (
      paymentFilterObj.statusValue !== filterObj.statusValue ||
      paymentFilterObj.txnId !== filterObj.txnId ||
      paymentFilterObj.orderId !== filterObj.orderId ||
      paymentFilterObj.internalOrderId !== filterObj.internalOrderId ||
      paymentFilterObj.merchantId !== filterObj.merchantId ||
      paymentFilterObj.amount !== filterObj.amount ||
      paymentFilterObj.dateSingleValue !== filterObj.dateSingleValue ||
      paymentFilterObj.dateRangeValue !== filterObj.dateRangeValue ||
      paymentFilterObj.dateValue !== filterObj.dateValue
    ) {
      dispatch(setPaymentFilterObj(filterObj));
      dispatch(
        setListingPagePropsObj({
          ...listingPageProps,
          activePage: 1,
        }),
      );
    }
    modalToggler();
  };

  const clearFunction = () => {
    setFilterObj(getInitialFilterState());
  };

  const dateRangeChildChangeHandler = useCallback((value: string | Date | Array<Date>) => {
    setFilterObj((prevState) => ({
      ...prevState,
      dateRangeValue: value,
    }));
  }, []);

  const dateChildChangeHandler = useCallback((value: Date | null | undefined | [Date | null, Date | null]) => {
    setFilterObj((prevState) => ({
      ...prevState,
      dateSingleValue: value,
    }));
  }, []);

  useEffect(() => {
    if (!filterObj.txnIdChecked && filterObj.txnId) {
      filterChangeHandler('txnId', '');
    }
  }, [filterObj.txnIdChecked, filterObj.txnId, filterChangeHandler]);

  useEffect(() => {
    if (!filterObj.orderIdChecked && filterObj.orderId) {
      filterChangeHandler('orderId', '');
    }
  }, [filterObj.orderIdChecked, filterObj.orderId, filterChangeHandler]);

  useEffect(() => {
    if (!filterObj.internalOrderIdChecked && filterObj.internalOrderId) {
      filterChangeHandler('internalOrderId', '');
    }
  }, [filterObj.internalOrderIdChecked, filterObj.internalOrderId, filterChangeHandler]);

  useEffect(() => {
    if (!filterObj.merchantIdChecked && filterObj.merchantId) {
      filterChangeHandler('merchantId', '');
    }
  }, [filterObj.merchantIdChecked, filterObj.merchantId, filterChangeHandler]);

  useEffect(() => {
    if (!filterObj.statusFilterChecked && filterObj.statusValue) {
      filterChangeHandler('statusValue', '');
    }
  }, [filterObj.statusFilterChecked, filterObj.statusValue, filterChangeHandler]);

  useEffect(() => {
    if (!filterObj.amountIdChecked && filterObj.amount) {
      filterChangeHandler('amount', '');
    }
  }, [filterObj.amountIdChecked, filterObj.amount, filterChangeHandler]);

  useEffect(() => {
    if (!filterObj.dateActiveChecked) {
      filterChangeHandler('dateTypeCompareValue', '');
      filterChangeHandler('dateTypeFilterValue', '');
      filterChangeHandler('dateValue', '');
      dateRangeChildChangeHandler('');
      dateChildChangeHandler(null);
    }
  }, [filterObj.dateActiveChecked, filterChangeHandler, dateRangeChildChangeHandler, dateChildChangeHandler]);

  return (
    <ClickAwayListener onClickAway={() => modalToggler()}>
      <div className={`filter-dropdown ${filterObj.dateTypeCompareValue === `is_between` ? `w-449` : `w-328`}`}>
        <div className="lp-flex lp-flex-column">
          <div className="filter-header">
            <Heading alignment="left" tagName="h2" variant="xs" className="mb-0">
              Filters
            </Heading>
            <Button
              type="button"
              onClick={() => modalToggler()}
              size="small"
              variant="ghost"
              className="button-close m-0 p-0"
            >
              <Icon name="Cross" className="m-0 p-0" />
            </Button>
          </div>
          <div className="filter-body">
            <CheckboxGroup>
              <div className="checkbox-items">
                <Checkbox
                  id="dateActiveChecked"
                  testId="payment-filter-date-check"
                  label="Date"
                  variant="body-2"
                  checked={filterObj.dateActiveChecked}
                  onChange={() => filterChangeHandler('dateActiveChecked', !filterObj.dateActiveChecked)}
                />
                {filterObj.dateActiveChecked && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full mb-16">
                      <div className="selectdropdown">
                        <Icon name="ChevronDown" className="arrow-down" />
                        <select
                          className="form-control"
                          value={filterObj.dateTypeCompareValue}
                          onChange={(e) => filterChangeHandler('dateTypeCompareValue', e.target.value)}
                          data-testid="payment-filter-date-select"
                        >
                          <option value="">please select</option>
                          <option value="is_in_the_last">is in the last</option>
                          <option value="is_equal_to">is equal to</option>
                          <option value="is_between">is between</option>
                          <option value="is_after">is after</option>
                          <option value="is_before">is before</option>
                        </select>
                      </div>
                      {filterObjError.dateTypeCompareError && (
                        <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                          {filterObjError.dateTypeCompareError}
                        </Text>
                      )}
                    </div>

                    {filterObj.dateTypeCompareValue ? (
                      <Fragment>
                        {filterObj.dateTypeCompareValue === 'is_in_the_last' && (
                          <Fragment>
                            <div className="lp-w-full lp-flex lp-align-center mb-0">
                              <div className="w-24">
                                <Icon name="Return" className="text-primary" />
                              </div>
                              <div className="w-40">
                                <input
                                  type="text"
                                  data-testid="payment-filter-date-value"
                                  className="form-control"
                                  value={filterObj.dateValue}
                                  onChange={(e) =>
                                    filterChangeHandler('dateValue', e.target.value.replace(/[^0-9]/g, ''))
                                  }
                                />
                              </div>
                              <div className="selectdropdown">
                                <Icon name="ChevronDown" className="arrow-down" />
                                <select
                                  className="form-control"
                                  data-testid="payment-filter-date-filter-value"
                                  value={filterObj.dateTypeFilterValue}
                                  onChange={(e) => filterChangeHandler('dateTypeFilterValue', e.target.value)}
                                >
                                  <option value="">please select</option>
                                  <option value="days">days</option>
                                  <option value="month">months</option>
                                </select>
                              </div>
                            </div>

                            {(filterObjError.dateValueError || filterObjError.dateTypeFilterError) && (
                              <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                                {filterObjError.dateValueError
                                  ? filterObjError.dateValueError
                                  : filterObjError.dateTypeFilterError}
                              </Text>
                            )}
                          </Fragment>
                        )}

                        {filterObj.dateTypeCompareValue !== 'is_in_the_last' && (
                          <Fragment>
                            <div className="lp-w-full lp-flex lp-align-center mb-0">
                              <div className="w-24">
                                <Icon name="Return" className="text-primary" />
                              </div>
                              <DateRangeFilterComponent
                                isSingleDateFeild={filterObj.dateTypeCompareValue !== 'is_between' ? true : false}
                                dateRangeChangeHandler={dateRangeChildChangeHandler}
                                dateChangeHandler={dateChildChangeHandler}
                                dateRangeValue={filterObj.dateRangeValue}
                                dateSingleValue={filterObj.dateSingleValue}
                              />
                            </div>

                            {(filterObjError.dateRangeError || filterObjError.dateSingleError) && (
                              <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                                {filterObjError.dateRangeError
                                  ? filterObjError.dateRangeError
                                  : filterObjError.dateSingleError}
                              </Text>
                            )}
                          </Fragment>
                        )}
                      </Fragment>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="checkbox-items">
                <Checkbox
                  id="statusFilterChecked"
                  testId="payment-filter-status-check"
                  label="Status"
                  variant="body-2"
                  checked={filterObj.statusFilterChecked}
                  onChange={() => filterChangeHandler('statusFilterChecked', !filterObj.statusFilterChecked)}
                />
                {filterObj.statusFilterChecked && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full mb-0">
                      <div className="selectdropdown">
                        <Icon name="ChevronDown" className="arrow-down" />
                        <select
                          className="form-control"
                          data-testid="payment-filter-status-select"
                          value={filterObj.statusValue}
                          onChange={(e) => filterChangeHandler('statusValue', e.target.value)}
                        >
                          <option value="">please select</option>
                          {Object.values(PaymentFilterStatusObj).map((obj, key) => (
                            <option value={obj.value} key={key}>
                              {obj.label}
                            </option>
                          ))}
                        </select>
                        {filterObjError.statusError && (
                          <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                            {filterObjError.statusError}
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="checkbox-items">
                <Checkbox
                  id="amountIdChecked"
                  testId="payment-filter-amount-check"
                  label="Amount"
                  variant="body-2"
                  checked={filterObj.amountIdChecked}
                  onChange={() => filterChangeHandler('amountIdChecked', !filterObj.amountIdChecked)}
                />
                {filterObj.amountIdChecked && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full filter-search position-relative mb-0">
                      <input
                        type="text"
                        data-testid="payment-filter-amount-input"
                        className="form-control"
                        placeholder="Enter amount"
                        value={filterObj.amount}
                        onChange={(e) => filterChangeHandler('amount', e.target.value.replace(/[^0-9.]+/g, ''))}
                      />
                      {/* <Button
                        type="button"
                        onClick={() => filterSaveHandler()}
                        size="small"
                        variant="ghost"
                        className="button-search"
                      >
                        <Icon name="Search" className="m-0 p-0 text-primary" />
                      </Button> */}
                    </div>
                    {filterObjError.amountError && (
                      <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                        {filterObjError.amountError}
                      </Text>
                    )}
                  </div>
                )}
              </div>

              <div className="checkbox-items">
                <Checkbox
                  id="merchantIdChecked"
                  testId="payment-filter-merchant-id-check"
                  label="Merchant ID"
                  variant="body-2"
                  checked={filterObj.merchantIdChecked}
                  onChange={() => filterChangeHandler('merchantIdChecked', !filterObj.merchantIdChecked)}
                />
                {filterObj.merchantIdChecked && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full filter-search position-relative mb-0">
                      <input
                        type="text"
                        data-testid="payment-filter-merchant-id-input"
                        className="form-control"
                        placeholder="Enter Merchant ID"
                        value={filterObj.merchantId}
                        onChange={(e) => filterChangeHandler('merchantId', e.target.value)}
                      />
                      {/* <Button
                        type="button"
                        onClick={() => filterSaveHandler()}
                        size="small"
                        variant="ghost"
                        className="button-search"
                      >
                        <Icon name="Search" className="m-0 p-0 text-primary" />
                      </Button> */}
                    </div>
                    {filterObjError.merchantIdError && (
                      <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                        {filterObjError.merchantIdError}
                      </Text>
                    )}
                  </div>
                )}
              </div>

              <div className="checkbox-items">
                <Checkbox
                  id="internalOrderIdChecked"
                  testId="payment-filter-internal-order-id-check"
                  label="Order ID"
                  variant="body-2"
                  checked={filterObj.internalOrderIdChecked}
                  onChange={() => filterChangeHandler('internalOrderIdChecked', !filterObj.internalOrderIdChecked)}
                />
                {filterObj.internalOrderIdChecked && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full filter-search position-relative mb-0">
                      <input
                        type="text"
                        data-testid="payment-filter-internal-order-id-input"
                        className="form-control"
                        placeholder="Enter Order ID"
                        value={filterObj.internalOrderId}
                        onChange={(e) => filterChangeHandler('internalOrderId', e.target.value)}
                      />
                    </div>
                    {filterObjError.internalOrderIdError && (
                      <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                        {filterObjError.internalOrderIdError}
                      </Text>
                    )}
                  </div>
                )}
              </div>

              {/* <div className="checkbox-items">
                <Checkbox
                  id="orderIdSelected"
                  testId="payment-filter-order-id-check"
                  label="Order ID"
                  variant="body-2"
                  checked={filterObj.orderIdSelected}
                  onChange={() => filterChangeHandler('orderIdSelected', !filterObj.orderIdSelected)}
                />
                {filterObj.orderIdSelected && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full filter-search position-relative mb-0">
                      <input
                        type="text"
                        data-testid="payment-filter-order-id-input"
                        className="form-control"
                        placeholder="Enter Order ID"
                        value={filterObj.orderId}
                        onChange={(e) => filterChangeHandler('orderId', e.target.value)}
                      />
                      <Button
                        type="button"
                        onClick={() => filterSaveHandler()}
                        size="small"
                        variant="ghost"
                        className="button-search"
                      >
                        <Icon name="Search" className="m-0 p-0 text-primary" />
                      </Button>
                    </div>
                    {filterObjError.orderIdError && (
                      <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                        {filterObjError.orderIdError}
                      </Text>
                    )}
                  </div>
                )}
              </div> */}

              <div className="checkbox-items">
                <Checkbox
                  id="txnIdChecked"
                  testId="payment-filter-txn-id-check"
                  label="Transaction ID"
                  variant="body-2"
                  checked={filterObj.txnIdChecked}
                  onChange={() => filterChangeHandler('txnIdChecked', !filterObj.txnIdChecked)}
                />
                {filterObj.txnIdChecked && (
                  <div className="checkbox-items-details">
                    <div className="lp-w-full filter-search position-relative mb-0">
                      <input
                        type="text"
                        data-testid="payment-filter-txn-id-input"
                        className="form-control"
                        placeholder="Enter Transaction ID"
                        value={filterObj.txnId}
                        onChange={(e) => filterChangeHandler('txnId', e.target.value)}
                      />
                      {/* <Button
                        type="button"
                        onClick={() => filterSaveHandler()}
                        size="small"
                        variant="ghost"
                        className="button-search"
                      >
                        <Icon name="Search" className="m-0 p-0 text-primary" />
                      </Button> */}
                    </div>
                    {filterObjError.txnIdError && (
                      <Text alignment="left" tagName="span" variant="caption" className="Mui-Error">
                        {filterObjError.txnIdError}
                      </Text>
                    )}
                  </div>
                )}
              </div>
            </CheckboxGroup>
          </div>
          <div className="filter-footer">
            <Button
              className="lp-w-full"
              onClick={() => clearFunction()}
              size="medium"
              variant="reversed"
              testId="payment-filter-clear-button"
              disabled={
                filterObj.dateActiveChecked ||
                filterObj.txnIdChecked ||
                filterObj.orderIdChecked ||
                filterObj.amountIdChecked ||
                filterObj.merchantIdChecked ||
                filterObj.statusFilterChecked ||
                filterObj.internalOrderIdChecked
                  ? false
                  : true
              }
            >
              Clear
            </Button>
            <Button
              className="lp-w-full"
              onClick={() => filterSaveHandler()}
              size="medium"
              variant="primary"
              testId="payment-filter-save-button"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}

export default FilterComponent;
